
import React from 'react';
import { FileText, Database, BarChart3, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const steps = [
  {
    icon: <FileText className="h-6 w-6 text-primary" />,
    title: "Inmatning av data",
    description: "Registrera upphandlingsdata, ladda upp relevanta dokument och kravspecifikationer."
  },
  {
    icon: <Database className="h-6 w-6 text-primary" />,
    title: "Kategorisering",
    description: "Systemet kategoriserar upphandlingen baserat på typ, omfattning och bransch."
  },
  {
    icon: <BarChart3 className="h-6 w-6 text-primary" />,
    title: "Avancerad analys",
    description: "Jämförelse med historiska data, konkurrensanalys och identifiering av kritiska faktorer."
  },
  {
    icon: <CheckCircle className="h-6 w-6 text-primary" />,
    title: "Rekommendationer",
    description: "Konkreta förslag, sammanfattning och strategiska råd för att optimera ditt anbud."
  }
];

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-20 bg-gradient-to-br from-blue-50 to-gray-100 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-100/30 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-blue-100/30 rounded-full blur-3xl"></div>
      
      {/* Decorative Patterns */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 right-10 w-32 h-32 border-4 border-dashed border-blue-300 rounded-full"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 border-4 border-dashed border-blue-300 rounded-full"></div>
        <div className="hidden md:block absolute top-1/4 left-1/3 w-16 h-16 border-2 border-blue-300 rounded-full"></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <div className="inline-block rounded-full bg-blue-100 px-4 py-1.5 mb-4 backdrop-blur-sm">
            <span className="text-sm font-montserrat font-bold text-darkgray">Arbetsprocess</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold mb-6 text-darkgray">Så här fungerar Snabbaupphandlingar</h2>
          <p className="text-lg text-darkgray/90 max-w-xl mx-auto font-opensans">
            Vår process hjälper dig att effektivisera arbetet med offentliga upphandlingar. Följ dessa fyra steg för att förbättra dina anbud.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-12">
          {steps.map((step, index) => (
            <div key={index} className="relative group">
              {/* Step number indicator */}
              <div className="absolute -top-4 -left-4 w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center text-darkgray font-montserrat font-bold text-sm z-20 shadow-lg group-hover:scale-110 transition-transform duration-300">
                {index + 1}
              </div>
              
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gray-200 z-10">
                  <div className="absolute top-1/2 left-0 transform -translate-y-1/2 w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                  <div className="absolute top-1/2 right-0 transform -translate-y-1/2 w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                </div>
              )}
              
              <div className="rounded-xl p-6 bg-white border border-gray-100 h-full shadow-md hover:shadow-lg transition-all duration-300 group-hover:-translate-y-1">
                <div className="mb-4">
                  {step.icon}
                </div>
                <h3 className="text-xl mb-3 font-montserrat font-bold text-darkgray">{step.title}</h3>
                <p className="text-darkgray/80 font-opensans">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Example Case Section */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 md:p-8 mb-12 shadow-md">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-montserrat font-bold text-darkgray mb-2">Exempel: Skolbygge i Halmstad</h3>
            <p className="text-darkgray/80 font-opensans">Se hur systemet analyserade en verklig upphandling</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <h4 className="text-lg font-montserrat font-bold text-darkgray mb-3">Inmatad information</h4>
              <ul className="text-darkgray/90 space-y-2 font-opensans">
                <li className="flex items-start gap-2">
                  <span className="bg-yellow-300 p-1 rounded-full flex-shrink-0 mt-1">
                    <CheckCircle className="h-3 w-3 text-darkgray" />
                  </span>
                  <span>Budget: 85 miljoner kronor</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-yellow-300 p-1 rounded-full flex-shrink-0 mt-1">
                    <CheckCircle className="h-3 w-3 text-darkgray" />
                  </span>
                  <span>Projekttyp: Nybyggnation av skola</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-yellow-300 p-1 rounded-full flex-shrink-0 mt-1">
                    <CheckCircle className="h-3 w-3 text-darkgray" />
                  </span>
                  <span>Särskilda krav: Miljöcertifiering, tillgänglighet</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-yellow-300 p-1 rounded-full flex-shrink-0 mt-1">
                    <CheckCircle className="h-3 w-3 text-darkgray" />
                  </span>
                  <span>Uppladdade dokument: Kravspecifikation, ritningar</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <h4 className="text-lg font-montserrat font-bold text-darkgray mb-3">Resultat</h4>
              <ul className="text-darkgray/90 space-y-2 font-opensans">
                <li className="flex items-start gap-2">
                  <span className="bg-yellow-300 p-1 rounded-full flex-shrink-0 mt-1">
                    <CheckCircle className="h-3 w-3 text-darkgray" />
                  </span>
                  <span>Insikter om prissättning baserat på liknande skolbyggen</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-yellow-300 p-1 rounded-full flex-shrink-0 mt-1">
                    <CheckCircle className="h-3 w-3 text-darkgray" />
                  </span>
                  <span>Identifiering av kritiska miljökrav och certifieringar</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-yellow-300 p-1 rounded-full flex-shrink-0 mt-1">
                    <CheckCircle className="h-3 w-3 text-darkgray" />
                  </span>
                  <span>Analys av konkurrentlandskapet och differentieringsmöjligheter</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-yellow-300 p-1 rounded-full flex-shrink-0 mt-1">
                    <CheckCircle className="h-3 w-3 text-darkgray" />
                  </span>
                  <span>Optimerad tidsplan för anbudsarbetet med kritiska milstolpar</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="text-center">
          <Button size="lg" className="bg-darkgray hover:bg-darkgray/90 text-white hover:text-white border-2 border-transparent hover:border-darkgray/20 shadow-lg font-montserrat font-bold">
            Kom igång nu
          </Button>
          <p className="mt-4 text-darkgray/80 text-sm font-opensans">
            Ingen kreditkortsinformation krävs. Prova gratis i 14 dagar.
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
